import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { useChatStore } from "../../../stores/chatStore";
import { formatDate } from "../../../helpers/dateUtils";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import { LupusIcon } from "../../../assets/icon";
import {
  containerStyles,
  buttonStyles,
  messageContainerStyles,
  textFieldSearchStyles,
} from "./styles";

interface SearchMessagesPanelProps {
  onMessageClick: (messageId: string, domain: string, focus: string) => void;
}

export const SearchMessagesPanel: React.FC<SearchMessagesPanelProps> = ({
  onMessageClick,
}) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const [localError, setLocalError] = useState("");

  const { fetchSearchResults, isLoadingSearch, errorSearch, searchResults } =
    useChatStore();

  const handleSearch = async () => {
    if (searchText.trim().length < 4) {
      setLocalError("Please enter at least 4 characters.");
      return;
    }
    setLocalError("");
    await fetchSearchResults(searchText);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (value.trim() === "") {
      useChatStore.setState({ searchResults: [] });
      setLocalError("");
    }
  };

  return (
    <Box sx={containerStyles}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          fullWidth
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LupusIcon style={{ color: "#06B6D4" }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldSearchStyles}
        />

        <Button variant="outlined" onClick={handleSearch} sx={buttonStyles}>
          Search
        </Button>
      </Box>

      {localError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {localError}
        </Typography>
      )}

      {isLoadingSearch && <Typography>Loading search results...</Typography>}
      {errorSearch && <Typography color="error">{errorSearch}</Typography>}
      {!isLoadingSearch && !errorSearch && searchResults.length === 0 && (
        <Typography>No results found.</Typography>
      )}

      {searchResults.length > 0 && (
        <Box display="flex" flexDirection="column" gap={2}>
          {searchResults.map((message) => (
            <Box
              key={message.message_id}
              sx={messageContainerStyles}
              onClick={() =>
                onMessageClick(
                  message.message_id,
                  message.domain,
                  message.focus
                )
              }
            >
              <Box>
                <CustomMarkdown>{message.text}</CustomMarkdown>
              </Box>
              <CustomTypography
                fontWeight={500}
                fontSize="12px"
                color={theme.palette.panelInfo.message.timeColor}
              >
                {formatDate(message.date)}
              </CustomTypography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
